'use client';
import React, { useEffect, useRef } from 'react';
import AppLogo from '@/components/ui/AppLogo';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: ErrorProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    console.error('[GlobalError]', error);
    // Move focus to heading for screen readers
    headingRef.current?.focus();
  }, [error]);

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center p-6"
      role="alert"
      aria-live="assertive"
    >
      <div className="max-w-md w-full text-center">
        <div className="flex justify-center mb-6">
          <AppLogo size={48} />
        </div>
        <div
          className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-6"
          aria-hidden="true"
        >
          <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-xl font-bold text-foreground mb-2 focus:outline-none"
        >
          Something went wrong
        </h1>
        <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
          An unexpected error occurred. Our team has been notified.
          {error?.digest && (
            <span className="block mt-1 font-mono text-xs text-muted-foreground/60">
              Error ID: {error.digest}
            </span>
          )}
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={reset}
            className="btn-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            Try Again
          </button>
          <a
            href="/"
            className="btn-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            Go Home
          </a>
        </div>
      </div>
    </div>
  );
}
